import { Chart, type ChartConfiguration, type TooltipItem } from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import type { SweepPoint } from '../ble/toolbox-protocol';

export type RetuneChartPhase = 'coarse' | 'fine';
export type RetuneRuntimeChartPhase = 'discover' | 'local_search' | 'track';
export type RetuneChartSelection = 'original' | 'updated' | null;
export type RetuneChartTargetOverlay = {
  targetId: string;
  label: string;
  centerHz: number;
  windowMinHz: number;
  windowMaxHz: number;
  previewMinHz: number;
  previewMaxHz: number;
};

export const RETUNE_SWEEP_LABEL = 'Retune sweep';
const RETUNE_Y_AXIS_GRACE = '24%';
const RETUNE_CHART_TOP_PADDING_PX = 0;
const LABEL_X_OFFSET_PX = 54;
const RETUNE_FALLBACK_SWEEP_MIN_HZ = 20000;
const RETUNE_FALLBACK_SWEEP_MAX_HZ = 60000;
const RETUNE_COARSE_LINE_COLOR = 'rgba(39, 58, 91, 0.42)';
const RETUNE_FINE_LINE_COLOR = '#f16b34';
const RETUNE_RUNTIME_DISCOVER_COLOR = 'rgba(88, 95, 102, 0.88)';
const RETUNE_RUNTIME_LOCAL_SEARCH_COLOR = '#2d9f73';
const RETUNE_RUNTIME_TRACK_COLOR = '#1f6fd1';
const RETUNE_RUNTIME_CENTER_LINE_COLOR = 'rgba(39, 58, 91, 0.9)';
const RETUNE_ORIGINAL_LINE_COLOR = 'rgba(88, 95, 102, 0.92)';
const RETUNE_ORIGINAL_LINE_COLOR_MUTED = 'rgba(88, 95, 102, 0.56)';
const RETUNE_UNSELECTED_TARGET_RANGE_BORDER = 'rgba(88, 95, 102, 0.24)';
const RETUNE_UNSELECTED_TARGET_RANGE_FILL = 'rgba(88, 95, 102, 0.08)';
const SELECTED_TARGET_LABEL_X_OFFSET_PX = 0;
const SELECTED_TARGET_LABEL_Y_OFFSET_PX = 0;
const RETUNE_TARGET_LABEL_Z = 6;
type LabelTextAlign = 'left' | 'right';

type BaseChartPoint = {
  x: number;
  y: number;
};

type ChartPoint = BaseChartPoint & {
  phase: RetuneChartPhase | RetuneRuntimeChartPhase;
};

type MarkerState = {
  initialHz: number | null;
  finalHz: number | null;
  selected: RetuneChartSelection;
};

type XRangeState = {
  sweepMinHz: number;
  sweepMaxHz: number;
};

type TargetOverlayState = {
  targets: RetuneChartTargetOverlay[];
  selectedTargetId: string | null;
  originalTargetId: string | null;
};

type RuntimeState = {
  enabled: boolean;
  centerHz: number | null;
  centerLabelVisible: boolean;
  markerY: number | null;
};

export type RetuneChartController = {
  reset: () => void;
  setSweepRange: (minHz: number, maxHz: number) => void;
  setTargetOverlays: (targets: RetuneChartTargetOverlay[]) => void;
  clearTargetOverlays: () => void;
  setSelectedTarget: (targetId: string | null) => void;
  setOriginalTarget: (targetId: string | null) => void;
  setInitialFrequency: (hz: number) => void;
  addPoint: (phase: RetuneChartPhase, point: SweepPoint) => void;
  setRuntimeMode: (enabled: boolean) => void;
  resetRuntime: () => void;
  addRuntimePoint: (phase: RetuneRuntimeChartPhase, point: SweepPoint) => void;
  setRuntimeCenterFrequency: (hz: number | null) => void;
  setFinalFrequency: (hz: number) => void;
  setSelectedMarker: (selection: RetuneChartSelection) => void;
  destroy: () => void;
};

let chartJsRegistered = false;
const patchedDescriptorFunctions = new WeakSet<Function>();

export function createRetuneChart(canvas: HTMLCanvasElement): RetuneChartController {
  ensureChartJsRegistration();

  const state: MarkerState = {
    initialHz: null,
    finalHz: null,
    selected: null,
  };
  const xRange: XRangeState = {
    sweepMinHz: RETUNE_FALLBACK_SWEEP_MIN_HZ,
    sweepMaxHz: RETUNE_FALLBACK_SWEEP_MAX_HZ,
  };
  const targetOverlays: TargetOverlayState = {
    targets: [],
    selectedTargetId: null,
    originalTargetId: null,
  };
  const runtime: RuntimeState = {
    enabled: false,
    centerHz: null,
    centerLabelVisible: false,
    markerY: null,
  };
  const coarsePoints: BaseChartPoint[] = [];
  const finePoints: BaseChartPoint[] = [];
  const runtimePoints: Record<RetuneRuntimeChartPhase, BaseChartPoint[]> = {
    discover: [],
    local_search: [],
    track: [],
  };

  const config = createConfig(state, targetOverlays, runtime);
  const chart = new Chart<'line', ChartPoint[]>(canvas, config);
  let destroyed = false;

  function buildDisplayedPoints(): ChartPoint[] {
    if (finePoints.length === 0) {
      return coarsePoints.map((point) => ({ ...point, phase: 'coarse' }));
    }

    const minFineFrequencyHz = finePoints[0].x;
    const maxFineFrequencyHz = finePoints[finePoints.length - 1].x;
    const coarseOutsideFineWindow = coarsePoints.filter(
      (point) => point.x < minFineFrequencyHz || point.x > maxFineFrequencyHz
    );
    const mergedPoints = coarseOutsideFineWindow
      .map((point) => ({ ...point, phase: 'coarse' as const }))
      .concat(finePoints.map((point) => ({ ...point, phase: 'fine' as const })));
    mergedPoints.sort((left, right) => left.x - right.x);
    return mergedPoints;
  }

  function syncRenderedDataset(): void {
    const renderedDataset = chart.data.datasets[0];
    if (!renderedDataset) {
      return;
    }
    renderedDataset.data = buildDisplayedPoints();
  }

  function syncRuntimeDatasets(): void {
    setDatasetData('discover', runtimePoints.discover);
    setDatasetData('local_search', runtimePoints.local_search);
    setDatasetData('track', runtimePoints.track);
  }

  function setDatasetData(phase: RetuneRuntimeChartPhase, points: BaseChartPoint[]): void {
    const dataset = chart.data.datasets.find((candidate) => candidate.label === getRuntimeDatasetLabel(phase));
    if (!dataset) {
      return;
    }
    dataset.data = points.map((point) => ({ ...point, phase }));
  }

  function applyRuntimeVisibility(): void {
    const sweepDataset = chart.data.datasets[0];
    if (sweepDataset) {
      sweepDataset.hidden = runtime.enabled;
    }

    (['discover', 'local_search', 'track'] as const).forEach((phase) => {
      const dataset = chart.data.datasets.find((candidate) => candidate.label === getRuntimeDatasetLabel(phase));
      if (dataset) {
        dataset.hidden = !runtime.enabled;
      }
    });
  }

  function queueChartUpdate(): void {
    if (destroyed) {
      return;
    }
    applyAnnotations(config, state, targetOverlays, runtime);
    applyXAxisBounds(config, xRange);
    applyRuntimeVisibility();
    chart.update('none');
  }

  function setMarker(key: keyof MarkerState, hz: number): void {
    if (!Number.isFinite(hz)) {
      return;
    }
    state[key] = Math.round(hz);
    queueChartUpdate();
  }

  return {
    reset: () => {
      state.initialHz = null;
      state.finalHz = null;
      state.selected = null;
      targetOverlays.targets = [];
      targetOverlays.selectedTargetId = null;
      targetOverlays.originalTargetId = null;
      runtime.centerHz = null;
      runtime.centerLabelVisible = false;
      runtime.markerY = null;
      coarsePoints.length = 0;
      finePoints.length = 0;
      runtimePoints.discover.length = 0;
      runtimePoints.local_search.length = 0;
      runtimePoints.track.length = 0;
      syncRenderedDataset();
      syncRuntimeDatasets();
      queueChartUpdate();
    },

    setSweepRange: (minHz: number, maxHz: number) => {
      if (!Number.isFinite(minHz) || !Number.isFinite(maxHz)) {
        return;
      }
      const roundedMin = Math.round(Math.min(minHz, maxHz));
      const roundedMax = Math.round(Math.max(minHz, maxHz));
      if (roundedMax <= roundedMin) {
        return;
      }
      if (xRange.sweepMinHz === roundedMin && xRange.sweepMaxHz === roundedMax) {
        return;
      }
      xRange.sweepMinHz = roundedMin;
      xRange.sweepMaxHz = roundedMax;
      queueChartUpdate();
    },

    setTargetOverlays: (targets: RetuneChartTargetOverlay[]) => {
      targetOverlays.targets = targets
        .filter((target) =>
          Number.isFinite(target.centerHz) &&
          Number.isFinite(target.windowMinHz) &&
          Number.isFinite(target.windowMaxHz) &&
          Number.isFinite(target.previewMinHz) &&
          Number.isFinite(target.previewMaxHz)
        )
        .map((target) => ({
          ...target,
          centerHz: Math.round(target.centerHz),
          windowMinHz: Math.round(Math.min(target.windowMinHz, target.windowMaxHz)),
          windowMaxHz: Math.round(Math.max(target.windowMinHz, target.windowMaxHz)),
          previewMinHz: Math.round(Math.min(target.previewMinHz, target.previewMaxHz)),
          previewMaxHz: Math.round(Math.max(target.previewMinHz, target.previewMaxHz)),
        }));

      if (
        targetOverlays.selectedTargetId &&
        !targetOverlays.targets.some((target) => target.targetId === targetOverlays.selectedTargetId)
      ) {
        targetOverlays.selectedTargetId = null;
      }
      if (
        targetOverlays.originalTargetId &&
        !targetOverlays.targets.some((target) => target.targetId === targetOverlays.originalTargetId)
      ) {
        targetOverlays.originalTargetId = null;
      }

      queueChartUpdate();
    },

    clearTargetOverlays: () => {
      targetOverlays.targets = [];
      targetOverlays.selectedTargetId = null;
      targetOverlays.originalTargetId = null;
      queueChartUpdate();
    },

    setSelectedTarget: (targetId: string | null) => {
      targetOverlays.selectedTargetId = targetId;
      queueChartUpdate();
    },

    setOriginalTarget: (targetId: string | null) => {
      targetOverlays.originalTargetId = targetId;
      queueChartUpdate();
    },

    setInitialFrequency: (hz: number) => {
      setMarker('initialHz', hz);
    },

    addPoint: (phase: RetuneChartPhase, point: SweepPoint) => {
      if (!Number.isFinite(point.frequencyHz) || !Number.isFinite(point.powerW)) {
        return;
      }

      if (
        targetOverlays.targets.length > 0 ||
        targetOverlays.selectedTargetId !== null ||
        targetOverlays.originalTargetId !== null
      ) {
        targetOverlays.targets = [];
        targetOverlays.selectedTargetId = null;
        targetOverlays.originalTargetId = null;
      }

      const frequencyHz = Math.round(point.frequencyHz);
      const phasePoints = phase === 'coarse' ? coarsePoints : finePoints;
      upsertPointSorted(phasePoints, {
        x: frequencyHz,
        y: point.powerW,
      });
      syncRenderedDataset();
      queueChartUpdate();
    },

    setRuntimeMode: (enabled: boolean) => {
      runtime.enabled = enabled;
      queueChartUpdate();
    },

    resetRuntime: () => {
      state.initialHz = null;
      state.finalHz = null;
      state.selected = null;
      runtime.centerHz = null;
      runtime.centerLabelVisible = false;
      runtime.markerY = null;
      runtimePoints.discover.length = 0;
      runtimePoints.local_search.length = 0;
      runtimePoints.track.length = 0;
      syncRuntimeDatasets();
      queueChartUpdate();
    },

    addRuntimePoint: (phase: RetuneRuntimeChartPhase, point: SweepPoint) => {
      if (!Number.isFinite(point.frequencyHz) || !Number.isFinite(point.powerW)) {
        return;
      }

      const phasePoints = runtimePoints[phase];
      runtime.markerY = Math.max(runtime.markerY ?? point.powerW, point.powerW);
      phasePoints.push({
        x: Math.round(point.frequencyHz),
        y: point.powerW,
      });
      if (phasePoints.length > 220) {
        phasePoints.splice(0, phasePoints.length - 220);
      }
      syncRuntimeDatasets();
      queueChartUpdate();
    },

    setRuntimeCenterFrequency: (hz: number | null) => {
      if (hz != null && !Number.isFinite(hz)) {
        return;
      }
      const nextCenterHz = hz == null ? null : Math.round(hz);
      if (runtime.centerHz === nextCenterHz) {
        return;
      }
      if (runtime.centerHz !== nextCenterHz) {
        runtime.centerLabelVisible = false;
      }
      runtime.centerHz = nextCenterHz;
      queueChartUpdate();
    },

    setFinalFrequency: (hz: number) => {
      setMarker('finalHz', hz);
    },

    setSelectedMarker: (selection: RetuneChartSelection) => {
      state.selected = selection;
      queueChartUpdate();
    },

    destroy: () => {
      destroyed = true;
      chart.destroy();
    },
  };
}

function createConfig(
  state: MarkerState,
  targetOverlays: TargetOverlayState,
  runtime: RuntimeState
): ChartConfiguration<'line', ChartPoint[]> {
  return {
    type: 'line',
    data: {
      datasets: [
        {
          label: RETUNE_SWEEP_LABEL,
          data: [],
          borderColor: RETUNE_COARSE_LINE_COLOR,
          backgroundColor: 'rgba(39, 58, 91, 0.12)',
          borderWidth: 2,
          pointRadius: () => 0,
          pointHoverRadius: () => 0,
          pointBackgroundColor: (context) =>
            isFinePhasePoint(context.raw) ? RETUNE_FINE_LINE_COLOR : RETUNE_COARSE_LINE_COLOR,
          segment: {
            borderColor: (context) =>
              isFinePhaseSegment(context.p0.raw, context.p1.raw)
                ? RETUNE_FINE_LINE_COLOR
                : RETUNE_COARSE_LINE_COLOR,
            borderWidth: (context) => (isFinePhaseSegment(context.p0.raw, context.p1.raw) ? 3 : 2),
          },
          tension: 0.15,
        },
        createRuntimeDataset('discover', RETUNE_RUNTIME_DISCOVER_COLOR),
        createRuntimeDataset('local_search', RETUNE_RUNTIME_LOCAL_SEARCH_COLOR),
        createRuntimeDataset('track', RETUNE_RUNTIME_TRACK_COLOR),
      ],
    },
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: RETUNE_CHART_TOP_PADDING_PX,
        },
      },
      parsing: {
        xAxisKey: 'x',
        yAxisKey: 'y',
      },
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      scales: {
        x: {
          type: 'linear',
          min: RETUNE_FALLBACK_SWEEP_MIN_HZ,
          max: RETUNE_FALLBACK_SWEEP_MAX_HZ,
          ticks: {
            color: 'rgba(39, 58, 91, 0.72)',
            callback: (value) => formatFrequencyTick(Number(value)),
          },
          grid: {
            color: 'rgba(39, 58, 91, 0.08)',
          },
          border: {
            color: 'rgba(39, 58, 91, 0.16)',
          },
        },
        y: {
          display: false,
          grace: RETUNE_Y_AXIS_GRACE,
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (items) => buildTooltipTitle(items),
            label: () => '',
          },
        },
        annotation: {
          clip: false,
          annotations: buildAnnotations(state, targetOverlays, runtime),
        } as unknown,
      },
    },
  };
}

function createRuntimeDataset(
  phase: RetuneRuntimeChartPhase,
  color: string
): ChartConfiguration<'line', ChartPoint[]>['data']['datasets'][number] {
  const hollow = phase === 'discover';
  return {
    label: getRuntimeDatasetLabel(phase),
    data: [],
    hidden: true,
    showLine: false,
    borderWidth: 0,
    pointStyle: 'circle',
    pointRadius: 2.8,
    pointHoverRadius: 4.2,
    pointBackgroundColor: hollow ? '#ffffff' : color,
    pointBorderColor: color,
    pointBorderWidth: hollow ? 1.25 : 1,
  };
}

function getRuntimeDatasetLabel(phase: RetuneRuntimeChartPhase): string {
  if (phase === 'discover') {
    return 'Discover samples';
  }
  if (phase === 'local_search') {
    return 'Local search samples';
  }
  return 'Track samples';
}

function buildTooltipTitle(items: TooltipItem<'line'>[]): string {
  const item = items[0];
  if (!item) {
    return '';
  }
  return formatFrequencyHz(item.parsed.x);
}

function ensureChartJsRegistration(): void {
  if (chartJsRegistered) {
    return;
  }
  hardenChartDescriptorCallbacks(Chart.defaults as unknown);
  Chart.register(annotationPlugin);
  chartJsRegistered = true;
}

function hardenChartDescriptorCallbacks(root: unknown): void {
  const stack: unknown[] = [root];
  const visited = new Set<unknown>();

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || typeof node !== 'object' || visited.has(node)) {
      continue;
    }
    visited.add(node);

    const record = node as Record<string, unknown>;
    wrapDescriptorCallback(record, '_scriptable');
    wrapDescriptorCallback(record, '_indexable');

    Object.keys(record).forEach((key) => {
      const value = record[key];
      if (value && typeof value === 'object') {
        stack.push(value);
      }
    });
  }
}

function wrapDescriptorCallback(record: Record<string, unknown>, key: '_scriptable' | '_indexable'): void {
  const candidate = record[key];
  if (typeof candidate !== 'function' || patchedDescriptorFunctions.has(candidate)) {
    return;
  }

  const wrapped = ((name: unknown, ...rest: unknown[]) => {
    if (typeof name !== 'string') {
      return false;
    }
    try {
      return Boolean(candidate.call(record, name, ...rest));
    } catch {
      return false;
    }
  }) as typeof candidate;

  patchedDescriptorFunctions.add(candidate);
  patchedDescriptorFunctions.add(wrapped);
  record[key] = wrapped;
}

function applyAnnotations(
  config: ChartConfiguration<'line', ChartPoint[]>,
  state: MarkerState,
  targetOverlays: TargetOverlayState,
  runtime: RuntimeState
): void {
  const plugins = config.options?.plugins as Record<string, unknown> | undefined;
  if (!plugins) {
    return;
  }

  const annotation = (plugins.annotation ?? {}) as Record<string, unknown>;
  annotation.annotations = buildAnnotations(state, targetOverlays, runtime);
  plugins.annotation = annotation;
}

function applyXAxisBounds(
  config: ChartConfiguration<'line', ChartPoint[]>,
  range: XRangeState
): void {
  const scales = config.options?.scales as Record<string, unknown> | undefined;
  if (!scales) {
    return;
  }

  const xScale = scales.x as Record<string, unknown> | undefined;
  if (!xScale) {
    return;
  }

  xScale.min = range.sweepMinHz;
  xScale.max = range.sweepMaxHz;
}

function buildAnnotations(
  state: MarkerState,
  targetOverlays: TargetOverlayState,
  runtime: RuntimeState
): Record<string, unknown> {
  const highlightOriginal = state.selected == null || state.selected === 'original';
  const highlightUpdated = state.selected == null || state.selected === 'updated';
  const layout = resolveLabelLayout(state.initialHz, state.finalHz);
  const targetAnnotations = buildTargetAnnotations(targetOverlays);

  return {
    ...targetAnnotations,
    runtimeCenterIndicator: buildRuntimeCenterIndicator(runtime),
    runtimeCenterLabel: buildRuntimeCenterHoverLabel(runtime),
    initialFrequencyHalo: buildFrequencyLine({
      hz: state.initialHz,
      label: 'Original',
      color: 'rgba(255, 255, 255, 0.98)',
      dashed: [],
      position: 'start',
      yAdjust: -10,
      xAdjust: layout.initialXAdjust,
      textAlign: layout.initialTextAlign,
      active: highlightOriginal,
      borderWidth: {
        active: 6,
        inactive: 5,
      },
      z: 3,
      showLabel: false,
    }),
    initialFrequency: buildFrequencyLine({
      hz: state.initialHz,
      label: 'Original',
      color: highlightOriginal ? RETUNE_ORIGINAL_LINE_COLOR : RETUNE_ORIGINAL_LINE_COLOR_MUTED,
      dashed: [],
      position: 'start',
      yAdjust: -10,
      xAdjust: layout.initialXAdjust,
      textAlign: layout.initialTextAlign,
      active: highlightOriginal,
      borderWidth: {
        active: 3,
        inactive: 2,
      },
      z: 4,
      showLabel: false,
    }),
    finalFrequency: buildFrequencyLine({
      hz: state.finalHz,
      label: 'Updated',
      color: highlightUpdated ? 'rgba(39, 58, 91, 0.96)' : 'rgba(39, 58, 91, 0.35)',
      dashed: [2, 2],
      position: 'end',
      yAdjust: 8,
      xAdjust: layout.finalXAdjust,
      textAlign: layout.finalTextAlign,
      active: highlightUpdated,
      borderWidth: {
        active: 3,
        inactive: 2,
      },
      z: 3,
    }),
  };
}

function buildTargetAnnotations(state: TargetOverlayState): Record<string, unknown> {
  const annotations: Record<string, unknown> = {};

  state.targets.forEach((target) => {
    const selected = state.selectedTargetId === target.targetId;
    annotations[`targetPreview:${target.targetId}`] = buildUnselectedPreviewAnnotation(target, selected);
    annotations[`targetCenter:${target.targetId}`] = buildTargetCenterAnnotation(target, selected);
  });

  const selectedTarget =
    state.selectedTargetId == null
      ? null
      : state.targets.find((target) => target.targetId === state.selectedTargetId) ?? null;

  annotations.selectedPreview = buildSelectedPreviewAnnotation(selectedTarget);
  annotations.selectedTargetLabel = buildSelectedTargetLabelAnnotation(selectedTarget);
  return annotations;
}

function buildRuntimeCenterIndicator(runtime: RuntimeState): Record<string, unknown> {
  if (runtime.centerHz == null) {
    return {
      type: 'point',
      display: false,
      xValue: 0,
      yValue: 0,
    };
  }

  return {
    type: 'point',
    display: true,
    xValue: runtime.centerHz,
    yValue: runtime.markerY ?? 1,
    yAdjust: -12,
    radius: 5,
    pointStyle: 'triangle',
    rotation: 180,
    backgroundColor: RETUNE_RUNTIME_CENTER_LINE_COLOR,
    borderColor: '#ffffff',
    borderWidth: 1,
    hitTolerance: 8,
    adjustScaleRange: false,
    z: 8,
    enter: () => {
      runtime.centerLabelVisible = true;
      return true;
    },
    leave: () => {
      runtime.centerLabelVisible = false;
      return true;
    },
  };
}

function buildRuntimeCenterHoverLabel(runtime: RuntimeState): Record<string, unknown> {
  if (runtime.centerHz == null) {
    return {
      type: 'label',
      display: false,
      xValue: 0,
      yValue: 0,
    };
  }

  return {
    type: 'label',
    display: () => runtime.centerLabelVisible,
    xValue: runtime.centerHz,
    yValue: runtime.markerY ?? 1,
    yAdjust: -34,
    content: `Center: ${formatFrequencyHz(runtime.centerHz)}`,
    color: '#ffffff',
    backgroundColor: RETUNE_RUNTIME_CENTER_LINE_COLOR,
    borderRadius: 4,
    padding: 4,
    font: {
      size: 10,
      weight: '700',
    },
    adjustScaleRange: false,
    z: 9,
  };
}

function buildUnselectedPreviewAnnotation(
  target: RetuneChartTargetOverlay,
  selected: boolean
): Record<string, unknown> {
  return {
    type: 'box',
    display: !selected,
    xMin: target.previewMinHz,
    xMax: target.previewMaxHz,
    yMin: 'start',
    yMax: 'end',
    backgroundColor: RETUNE_UNSELECTED_TARGET_RANGE_FILL,
    borderColor: RETUNE_UNSELECTED_TARGET_RANGE_BORDER,
    borderWidth: 1,
    z: 0,
  };
}

function buildTargetCenterAnnotation(target: RetuneChartTargetOverlay, selected: boolean): Record<string, unknown> {
  return {
    type: 'line',
    display: selected,
    xMin: target.centerHz,
    xMax: target.centerHz,
    borderColor: 'rgba(241, 107, 52, 0.86)',
    borderWidth: 2,
    borderDash: [4, 3],
    z: 1,
    label: undefined,
  };
}

function buildSelectedPreviewAnnotation(
  target: RetuneChartTargetOverlay | null
): Record<string, unknown> {
  if (!target) {
    return {
      type: 'box',
      display: false,
      xMin: 0,
      xMax: 0,
    };
  }

  return {
    type: 'box',
    display: true,
    xMin: target.previewMinHz,
    xMax: target.previewMaxHz,
    yMin: 'start',
    yMax: 'end',
    backgroundColor: 'rgba(241, 107, 52, 0.10)',
    borderColor: 'rgba(241, 107, 52, 0.94)',
    borderWidth: 1,
    z: 2,
  };
}

function buildSelectedTargetLabelAnnotation(
  target: RetuneChartTargetOverlay | null
): Record<string, unknown> {
  if (!target) {
    return {
      type: 'label',
      display: false,
      xValue: 0,
      yValue: 0,
    };
  }

  return {
    type: 'label',
    display: true,
    xValue: target.previewMinHz,
    yValue: 1,
    yMin: 'start',
    yMax: 'start',
    radius: 0,
    position: {
      x: 'start',
      y: 'start',
    },
    content: target.label,
    color: '#ffffff',
    backgroundColor: 'rgba(241, 107, 52, 0.86)',
    padding: 4,
    xAdjust: SELECTED_TARGET_LABEL_X_OFFSET_PX,
    yAdjust: SELECTED_TARGET_LABEL_Y_OFFSET_PX,
    font: {
      size: 10,
      weight: '700',
    },
    z: RETUNE_TARGET_LABEL_Z,
  };
}

function buildFrequencyLine(input: {
  hz: number | null;
  label: string;
  color: string;
  dashed: number[];
  position: 'start' | 'end';
  yAdjust: number;
  xAdjust: number;
  textAlign: LabelTextAlign;
  active: boolean;
  borderWidth?: {
    active: number;
    inactive: number;
  };
  z?: number;
  showLabel?: boolean;
  labelBackgroundColor?: string;
}): Record<string, unknown> {
  if (input.hz == null) {
    return {
      type: 'line',
      display: false,
      xMin: 0,
      xMax: 0,
    };
  }

  return {
    type: 'line',
    display: true,
    xMin: input.hz,
    xMax: input.hz,
    borderColor: input.color,
    borderWidth: input.active
      ? (input.borderWidth?.active ?? 3)
      : (input.borderWidth?.inactive ?? 2),
    borderDash: input.dashed,
    z: input.z ?? (input.active ? 3 : 2),
    label:
      input.showLabel === false
        ? undefined
        : {
            display: true,
            content: `${input.label}: ${formatFrequencyHz(input.hz)}`,
            position: input.position,
            color: '#ffffff',
            backgroundColor: input.labelBackgroundColor ?? input.color,
            padding: 4,
            yAdjust: input.yAdjust,
            xAdjust: input.xAdjust,
            textAlign: input.textAlign,
            font: {
              size: 10,
              weight: input.active ? '700' : '500',
            },
          },
  };
}

function resolveLabelLayout(
  initialHz: number | null,
  finalHz: number | null
): {
  initialXAdjust: number;
  finalXAdjust: number;
  initialTextAlign: LabelTextAlign;
  finalTextAlign: LabelTextAlign;
} {
  const leftLayout = {
    xAdjust: -LABEL_X_OFFSET_PX,
    textAlign: 'right' as LabelTextAlign,
  };
  const rightLayout = {
    xAdjust: LABEL_X_OFFSET_PX,
    textAlign: 'left' as LabelTextAlign,
  };

  if (initialHz == null || finalHz == null) {
    return {
      initialXAdjust: leftLayout.xAdjust,
      finalXAdjust: rightLayout.xAdjust,
      initialTextAlign: leftLayout.textAlign,
      finalTextAlign: rightLayout.textAlign,
    };
  }

  if (initialHz <= finalHz) {
    return {
      initialXAdjust: leftLayout.xAdjust,
      finalXAdjust: rightLayout.xAdjust,
      initialTextAlign: leftLayout.textAlign,
      finalTextAlign: rightLayout.textAlign,
    };
  }

  return {
    initialXAdjust: rightLayout.xAdjust,
    finalXAdjust: leftLayout.xAdjust,
    initialTextAlign: rightLayout.textAlign,
    finalTextAlign: leftLayout.textAlign,
  };
}

function formatFrequencyTick(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }

  const rounded = Math.round(value);
  if (Math.abs(rounded) >= 1000) {
    const khz = rounded / 1000;
    return `${khz.toFixed(1)} kHz`;
  }
  return `${rounded} Hz`;
}

function formatFrequencyHz(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} Hz`;
}

function upsertPointSorted(points: BaseChartPoint[], point: BaseChartPoint): void {
  let index = 0;
  while (index < points.length && points[index].x < point.x) {
    index += 1;
  }

  if (index < points.length && points[index].x === point.x) {
    points[index] = point;
    return;
  }

  points.splice(index, 0, point);
}

function isFinePhasePoint(raw: unknown): boolean {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'phase' in raw &&
    (raw as { phase?: RetuneChartPhase }).phase === 'fine'
  );
}

function isFinePhaseSegment(startRaw: unknown, endRaw: unknown): boolean {
  return isFinePhasePoint(startRaw) || isFinePhasePoint(endRaw);
}
